export const skillGroups = [
  {
    category: "Programming Languages",
    skills: ["Python", "Go", "C++"],
  },
  {
    category: "Cloud & Infrastructure",
    skills: [
      "Kubernetes (GKE)",
      "Terraform",
      "Google Cloud Platform",
      "Microsoft Azure",
      "Consul",
      "HashiCorp Vault",
    ],
  },
  {
    category: "CI/CD & GitOps",
    skills: ["ArgoCD", "Jenkins", "Helm", "GitOps", "Renovate", "Docker"],
  },
  {
    category: "AI & LLM Infrastructure",
    skills: [
      "vLLM",
      "SGLang",
      "Onyx / Danswer",
      "MCP Servers",
      "GPU Scheduling",
      "Apigee",
      "Kong",
    ],
  },
  {
    category: "Data & Messaging",
    skills: [
      "Elasticsearch",
      "Kafka",
      "Logstash",
      "BigQuery",
      "Pub/Sub",
      "Filebeat",
    ],
  },
  {
    category: "Observability",
    skills: [
      "Prometheus",
      "Grafana",
      "Victoria Metrics",
      "Victoria Logs",
      "Kibana",
      "KEDA",
    ],
  },
];

export const experiences = [
  {
    id: "egnyte-sde2",
    company: "Egnyte",
    role: "DevOps Engineer (Software Engineer II)",
    period: "Jan 2026 – Present",
    location: "Remote, India",
    highlights: [
      "Built an LLM-based Kubernetes Debugging Agent for natural-language k8s and VM troubleshooting across all environments including FedRAMP; gave internal tech talk to the engineering team.",
      "Deployed and maintained a fleet of MCP servers in production (k8s-mcp, grafana-mcp, vlogs-mcp, procore-mcp, deltek-mcp) across multi-cluster GKE with RBAC and network policies.",
      "Deployed and operated Egnyte's enterprise AI search platform (Onyx/Danswer) across dev, QA, global-prod, and FedRAMP; integrated GPT-5, Gemini 3, Claude, and Qwen models.",
      "Managed LLM serving infra (SGLang, vLLM) for Qwen, Gemma, GPT-5.x, and Claude on GPU node pools; built Grafana dashboards tracking SGLang Prometheus metrics.",
      "Led full API gateway migration from Kong to Apigee: response/header/regex-based rate limiting, IP restriction, and domain whitelisting.",
      "Stood up a new GKE perf cluster (us-west1) from scratch with bastion host, ArgoCD, Consul, Vault, and Jenkins pipelines.",
      "Onboarded 4 engineers, authored team runbooks, and built an AI-augmented deployment system with Claude/Augment Code that cut onboarding from days to hours.",
    ],
  },
  {
    id: "egnyte-sde1",
    company: "Egnyte",
    role: "Software Engineer, Production Engineering",
    period: "Aug 2023 – Dec 2025",
    location: "Remote, India",
    highlights: [
      "Drove KEDA autoscaling and GKE Compute Class migrations that saved $10K in one month with $100K+ projected annual reduction.",
      "Owned FedRAMP security remediation: patched 100+ Critical/High/Medium CVEs (kernel, Python, OpenSSL) and enforced SELinux and fapolicy hardening for ATO compliance.",
      "Zero-downtime Vertex AI to Gemini embedding migration across the production embedding platform; managed ES version upgrades and reindex pipelines across the entire fleet.",
      "Migrated the logging stack from ELK to Victoria Logs (VLK) and managed the filebeat to Kafka to Logstash to ES pipeline.",
      "Built K8s Descheduler automation in Python for spot-node pre-scheduling, integrated with Prometheus metrics and in-house image builds.",
      "Migrated multiple services from Helm to ArgoCD-based GitOps delivery across QA and production clusters.",
      "Responded to 100+ production incidents across multi-region and DR environments; partnered with SRE teams on RCA and automation.",
      "Recognised as Egnyter of the Quarter (Q2 2025) for collaborative and proactive contributions across AI services and production engineering.",
    ],
  },
  {
    id: "gfg",
    company: "GeeksforGeeks",
    role: "Software Engineer (Frontend) + Technical Content",
    period: "Aug 2022 – Mar 2023",
    location: "Noida, India",
    highlights: [
      "Built frontend components with React and Next.js, participated in testing and code reviews.",
      "Authored 100+ C/C++ and DSA articles that generated 9.5M+ views across the platform.",
    ],
  },
  {
    id: "scaler",
    company: "Scaler",
    role: "Mentor, Data Structures and Algorithms",
    period: "May 2021 – Nov 2021",
    location: "Bengaluru, India",
    highlights: [
      "Mentored students on DSA, resume feedback, and interview prep while the community scaled from 1K to 100K+ members.",
    ],
  },
];

export const achievements = [
  {
    title: "Egnyter of the Quarter (Q2 2025)",
    description:
      "Recognised for impact across AI infrastructure and production engineering at Egnyte.",
  },
  {
    title: "LeetCode Knight (Top 2.1%)",
    description:
      "Max Rating 2050. Solved 800+ problems with a 498-day streak. Global Rank 107 and India Rank 13 in Biweekly Contest 75.",
    link: "https://leetcode.com/k_joshi/",
  },
  {
    title: "CodeChef 5 Star (Max Rating 2014)",
    description:
      "Global Rank 25 and India Rank 20 in July Lunchtime 2022.",
    link: "https://www.codechef.com/users/kjoshi2001",
  },
  {
    title: "Codeforces Specialist (Max Rating 1446)",
    description:
      "Solved 700+ problems. Global Rank 176 and India Rank 15 in Codeforces Round 835 (Div. 4).",
    link: "https://codeforces.com/profile/kjoshi_",
  },
  {
    title: "IEEEXtreme 14.0",
    description: "Global Rank 278 and All India Rank 21.",
    cert: "IEEEXtreme-14.0.pdf",
  },
  {
    title: "IEEEXtreme 15.0",
    description: "Global Rank 484 and All India Rank 86.",
    cert: "IEEEXtreme-15.0.pdf",
  },
  {
    title: "1000+ DSA Problems Solved",
    description:
      "Across LeetCode, Codeforces, and CodeChef combined. Highest rated competitive programmer in college.",
  },
  {
    title: "GeeksforGeeks Author",
    description:
      "100+ technical articles on C/C++ and DSA with 9.5M+ total views.",
    link: "https://auth.geeksforgeeks.org/user/kamleshjoshi18/articles",
  },
];

export const certifications = [
  {
    title: "Google Cloud Certified Associate Cloud Engineer",
    issuer: "Google Cloud",
    date: "2024",
    type: "image",
    file: "AssociateCloudEngineer_cert.png",
  },
  {
    title: "GCP Cloud Digital Leader",
    issuer: "Google Cloud",
    date: "2024",
    type: "credential",
  },
  {
    title: "IEEEXtreme 15.0 - Competitive Programming",
    issuer: "IEEE",
    date: "2023",
    type: "pdf",
    file: "IEEEXtreme-15.0.pdf",
    description: "Global Rank 484, All India Rank 86",
  },
  {
    title: "IEEEXtreme 14.0 - Competitive Programming",
    issuer: "IEEE",
    date: "2023",
    type: "pdf",
    file: "IEEEXtreme-14.0.pdf",
    description: "Global Rank 278, All India Rank 21",
  },
  {
    title: "Google Kickstart 2022",
    issuer: "Google",
    date: "2022",
    type: "pdf",
    file: "Google-kickstart-2022.pdf",
  },
  {
    title: "Google Kickstart 2021",
    issuer: "Google",
    date: "2021",
    type: "pdf",
    file: "google-kickstart-2021.pdf",
  },
  {
    title: "Google Kickstart 2020",
    issuer: "Google",
    date: "2020",
    type: "pdf",
    file: "google-Kickstart-2020.pdf",
  },
  {
    title: "Hash Code 2022",
    issuer: "Google",
    date: "2022",
    type: "pdf",
    file: "HashCode-22.pdf",
  },
  {
    title: "Hash Code 2021",
    issuer: "Google",
    date: "2021",
    type: "pdf",
    file: "HashCode-21.pdf",
  },
  {
    title: "Kubernetes - Hands-on Tutorial",
    issuer: "Kubernetes",
    date: "2024",
    type: "credential",
  },
  {
    title: "Docker Course",
    issuer: "Docker",
    date: "2023",
    type: "credential",
  },
];

export const profiles = [
  {
    platform: "LeetCode",
    handle: "k_joshi",
    stat: "Knight · Max Rating 2050 · Top 2.1% worldwide",
    link: "https://leetcode.com/k_joshi/",
  },
  {
    platform: "Codeforces",
    handle: "kjoshi_",
    stat: "Specialist · Max Rating 1446",
    link: "https://codeforces.com/profile/kjoshi_",
  },
  {
    platform: "CodeChef",
    handle: "kjoshi2001",
    stat: "5★ · Max Rating 2014 · Global Rank 25 (July Lunchtime 2022)",
    link: "https://www.codechef.com/users/kjoshi2001",
  },
  {
    platform: "GitHub",
    handle: "kamleshjoshi8102",
    stat: "Projects, automation scripts & experiments",
    link: "https://github.com/kamleshjoshi8102",
  },
  {
    platform: "GeeksforGeeks",
    handle: "kamleshjoshi18",
    stat: "450+ problems · 100+ articles · 9.5M+ views",
    link: "https://auth.geeksforgeeks.org/user/kamleshjoshi18/articles",
  },
  {
    platform: "LinkedIn",
    handle: "kamlesh-joshi-",
    stat: "Let's connect",
    link: "https://www.linkedin.com/in/kamlesh-joshi-/",
  },
];

const projects = [
  {
    name: "Kubernetes Debugging Agent",
    description:
      "LLM-based agent for natural-language k8s and VM troubleshooting. Built prompt logic and RBAC across all envs including FedRAMP DCs. Gave internal tech talk on it.",
    tags: ["Python", "Kubernetes", "LLM", "MCP", "RBAC"],
    source_code_link: "https://github.com/kamleshjoshi8102",
  },
  {
    name: "MCP Server Fleet",
    description:
      "Deployed and maintained k8s-mcp, grafana-mcp, vlogs-mcp, procore-mcp, and deltek-mcp across multi-cluster GKE in global prod with RBAC and network policies.",
    tags: ["Kubernetes", "GCP", "MCP", "RBAC", "Helm"],
    source_code_link: "https://github.com/kamleshjoshi8102",
  },
  {
    name: "Kong to Apigee Migration",
    description:
      "Full API gateway migration covering response/header/regex-based rate limiting, IP restriction, and domain whitelisting across all production traffic.",
    tags: ["Apigee", "Kong", "GCP", "API Gateway"],
    source_code_link: "https://github.com/kamleshjoshi8102",
  },
  {
    name: "Onyx Enterprise AI Platform",
    description:
      "Deployed and operated Egnyte's AI search platform (Onyx/Danswer) across dev/qa/global-prod and FedRAMP. Integrated GPT-5, Gemini 3, Claude, and Qwen. Built custom agent and fixed IAP/network-policy bugs.",
    tags: ["Docker", "Kubernetes", "GCP", "LLM", "Python"],
    source_code_link: "https://github.com/kamleshjoshi8102",
  },
  {
    name: "Perf Cluster Build-out",
    description:
      "Stood up a new GKE cluster (us-west1) from scratch: bastion host, ArgoCD, Consul, Vault, and Jenkins pipelines. Full networking and repo setup.",
    tags: ["GKE", "Terraform", "ArgoCD", "Consul", "Vault"],
    source_code_link: "https://github.com/kamleshjoshi8102",
  },
  {
    name: "K8s Descheduler Automation",
    description:
      "Custom Python tool for spot-node pre-scheduling integrated with Prometheus metrics and in-house image builds. Reduces pod disruptions on preemptible nodes.",
    tags: ["Python", "Kubernetes", "Prometheus", "Docker"],
    source_code_link: "https://github.com/kamleshjoshi8102",
  },
  {
    name: "LeetCode API",
    description:
      "Node.js API wrapper around LeetCode profiles. Scrapes and exposes user stats including problem counts, ratings, submission calendar, and contribution points.",
    tags: ["Node.js", "Express", "API", "Scraping"],
    source_code_link: "https://github.com/kamleshjoshi8102/lcapi",
  },
  {
    name: "Chat App",
    description:
      "Real-time chat application using React and Socket.io on the frontend with Node.js/Express backend. Seamless WebSocket communication with user presence and notifications.",
    tags: ["React", "Socket.io", "Node.js", "Express", "MongoDB"],
    source_code_link: "https://github.com/kamleshjoshi8102/Chat-App-Frontend",
  },
  {
    name: "Braille to English Translator",
    description:
      "ML-based web app that converts Braille text to English. Flask backend with trained neural network model. React frontend with intuitive UI for image/text upload and real-time translation.",
    tags: ["Flask", "Python", "Machine Learning", "React", "TensorFlow"],
    source_code_link: "https://github.com/kamleshjoshi8102/Braille2English",
  },
  {
    name: "Recipe Hub",
    description:
      "Full-stack recipe web app where users can create, explore, and save recipes. MERN stack with persistent storage and user authentication.",
    tags: ["React", "Node.js", "Express", "MongoDB"],
    source_code_link: "https://github.com/kamleshjoshi8102/frontend-recipe",
  },
];

export { projects };
